"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowRight, Cpu, Globe, Sparkles, ChevronRight, Layers, Bot, Route, Zap, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface StepItem {
  vi: string;
  en: string;
}
interface CourseColumn {
  code: string;
  titleVi: string;
  titleEn: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  accentColor: string;
  badgeStyle: string;
  steps: StepItem[];
  linkTo: string;
}
interface PipelineStep {
  id: string;
  stepNum: string;
  phaseVi: string;
  phaseEn: string;
  titleVi: string;
  titleEn: string;
  descVi: string;
  descEn: string;
  techVi: string;
  techEn: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const PIPELINE_STEPS: PipelineStep[] = [{
  id: "sensor",
  stepNum: "01",
  phaseVi: "INPUT • CẢM BIẾN",
  phaseEn: "INPUT • SENSOR",
  titleVi: "1. Thu Nhận Dữ Liệu (Sensor)",
  titleEn: "1. Sensor Data Acquisition",
  descVi: "Cảm biến liên tục đo đạc môi trường xung quanh: nhận diện vạch kẻ đường, tính khoảng cách vật cản và vị trí robot.",
  descEn: "Sensors continuously scan environment: detect road lines, calculate obstacle distance, and measure vehicle orientation.",
  techVi: "Sóng siêu âm • Dò line • Gyroscope • Encoder",
  techEn: "Ultrasonic • Line Array • Gyroscope • Encoder",
  icon: Radio
}, {
  id: "decision",
  stepNum: "02",
  phaseVi: "LOGIC • XỬ LÝ",
  phaseEn: "LOGIC • DECISION",
  titleVi: "2. Phân Tích & Quyết Định",
  titleEn: "2. Decision & Algorithm",
  descVi: "Bộ vi điều khiển xử lý thuật toán PID và logic điều kiện để tính toán phương án di chuyển và tránh vật cản tối ưu.",
  descEn: "Microcontroller executes PID loops and logic state machines to calculate velocity vectors and optimal trajectories.",
  techVi: "Thuật toán PID • Logic If-Else • Lập lộ trình",
  techEn: "PID Control • State Machine • Pathfinding",
  icon: Cpu
}, {
  id: "action",
  stepNum: "03",
  phaseVi: "OUTPUT • HÀNH ĐỘNG",
  phaseEn: "OUTPUT • ACTION",
  titleVi: "3. Thực Thi Hành Động",
  titleEn: "3. Motor Actuation",
  descVi: "Chuyển mã lệnh thành tín hiệu điều xung PWM đến động cơ và servo, giúp xe tăng tốc, bẻ lái hoặc dừng khẩn cấp.",
  descEn: "Converts algorithmic decisions into PWM motor power and servo angles for smooth steering and automatic braking.",
  techVi: "Điều xung PWM • Động cơ DC/Servo • Phanh tự động",
  techEn: "PWM Control • DC/Servo Motors • Auto Braking",
  icon: Zap
}];
const COURSES_DATA: CourseColumn[] = [{
  code: "HP2",
  titleVi: "Robot biết cảm nhận",
  titleEn: "Sensing Robots",
  icon: Cpu,
  accentColor: "border-primary/20 hover:border-primary/60",
  badgeStyle: "bg-primary/10 text-primary border-primary/20",
  steps: [{
    vi: "Cảm biến",
    en: "Sensors"
  }, {
    vi: "Điều kiện",
    en: "Conditions"
  }, {
    vi: "Phản hồi",
    en: "Response"
  }],
  linkTo: "/RoboticsCourseHP2Page"
}, {
  code: "HP3",
  titleVi: "Xe robot đầu tiên",
  titleEn: "First Robot Car",
  icon: Bot,
  accentColor: "border-secondary/30 hover:border-secondary/80",
  badgeStyle: "bg-secondary/15 text-secondary-foreground border-secondary/30",
  steps: [{
    vi: "Khoảng cách",
    en: "Distance"
  }, {
    vi: "Góc quay",
    en: "Turning angle"
  }, {
    vi: "Điều khiển chuyển động",
    en: "Motion control"
  }],
  linkTo: "/RoboticsCourseHP3Page"
}, {
  code: "HP4",
  titleVi: "Xe robot thông minh",
  titleEn: "Smart Mobile Robot",
  icon: Zap,
  accentColor: "border-accent/30 hover:border-accent/80",
  badgeStyle: "bg-accent/20 text-foreground border-accent/40",
  steps: [{
    vi: "Va chạm",
    en: "Collision"
  }, {
    vi: "Vật cản",
    en: "Obstacles"
  }, {
    vi: "Phản ứng",
    en: "Reaction"
  }],
  linkTo: "/RoboticsCourseHP4Page"
}, {
  code: "HP5",
  titleVi: "Thành phố xe tự lái",
  titleEn: "Autonomous City Car",
  icon: Route,
  accentColor: "border-chart-1/30 hover:border-chart-1/80",
  badgeStyle: "bg-chart-1/15 text-foreground border-chart-1/30",
  steps: [{
    vi: "Dò line",
    en: "Line following"
  }, {
    vi: "Ngã tư",
    en: "Intersections"
  }, {
    vi: "Lập lộ trình",
    en: "Route planning"
  }, {
    vi: "Đường ngắn nhất",
    en: "Shortest path"
  }],
  linkTo: "/RoboticsCourseHp5Page"
}];
export default function SensorToAutonomousJourney() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const savedLang = localStorage.getItem("roboto_lang");
    if (savedLang === "vi" || savedLang === "en") {
      setLang(savedLang);
    }
    const handleLangChange = (e: CustomEvent<{
      lang?: "vi" | "en";
    }> | Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "vi" | "en";
      }>;
      const detailLang = customEvent.detail?.lang || localStorage.getItem("roboto_lang");
      if (detailLang === "vi" || detailLang === "en") {
        setLang(detailLang);
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
    };
  }, []);
  const toggleLanguage = (selectedLang: "vi" | "en") => {
    setLang(selectedLang);
    localStorage.setItem("roboto_lang", selectedLang);
    window.dispatchEvent(new CustomEvent("roboto-lang-change", {
      detail: {
        lang: selectedLang
      }
    }));
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="sensortoautonomousjourney" data-nav="light" className="relative py-16 md:py-24 bg-background text-foreground overflow-hidden">
      {/* Decorative subtle background shapes */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden aria-hidden" aria-hidden="true">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-secondary/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Top Header Controls & Section Intro */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-10 md:mb-14">
          {/* Bilingual Language Switcher Pill */}
          <div className="inline-flex items-center gap-1 p-1 mb-6 rounded-full bg-muted border border-border shadow-2xs">
            <Globe className="size-3.5 text-muted-foreground ml-2 mr-1" />
            <Button type="button" variant={lang === "vi" ? "default" : "ghost"} size="sm" onClick={() => toggleLanguage("vi")} className="rounded-full text-xs h-7 px-3 font-medium transition-all">
              Tiếng Việt
            </Button>
            <Button type="button" variant={lang === "en" ? "default" : "ghost"} size="sm" onClick={() => toggleLanguage("en")} className="rounded-full text-xs h-7 px-3 font-medium transition-all">
              English
            </Button>
          </div>

          <Badge variant="outline" className="mb-3 px-3 py-1 gap-1.5 border-primary/30 bg-primary/5 text-primary">
            <Layers className="size-3.5" />
            {lang === "vi" ? "Lộ trình kỹ thuật chuyên sâu" : "Technical Progression Path"}
          </Badge>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-serif tracking-tight text-foreground text-balance">
            {lang === "vi" ? "TỪ CẢM BIẾN ĐẾN ROBOT TỰ HÀNH" : "FROM SENSORS TO AUTONOMOUS ROBOTS"}
          </h2>

          <p className="mt-3 text-muted-foreground text-sm sm:text-base leading-relaxed max-w-2xl text-balance">
            {lang === "vi" ? "Hành trình phát triển tư duy lập trình và kỹ thuật qua các cấp độ HP2 - HP5, giúp học viên từng bước làm chủ công nghệ xe tự lái thông minh." : "A step-by-step technical progression from HP2 to HP5, guiding young learners from basic sensing principles to advanced autonomous vehicle control."}
          </p>
        </div>

        {/* Technical Deep-Blue Blueprint Diagram: Sensor -> Decision -> Action */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45
      }} className="mb-12 md:mb-16 rounded-2xl bg-[#091120] text-slate-100 border border-slate-800 shadow-xl p-6 sm:p-8 md:p-10 relative overflow-hidden">
          {/* Blueprint Grid & Glow Effect */}
          <div className="absolute inset-0 bg-[radial-gradient(#38bdf8_1px,transparent_1px)] [background-size:20px_20px] opacity-[0.08] pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-sky-400 to-primary" />

          {/* Diagram Header Banner */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-6 border-b border-slate-800/80 relative z-10">
            <div>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded bg-sky-950/80 border border-sky-800/60 text-sky-400 text-xs font-mono font-bold tracking-wider uppercase mb-2">
                <span className="size-2 rounded-full bg-sky-400 animate-pulse" />
                {lang === "vi" ? "KIẾN TRÚC TỰ HÀNH KÍCH HOẠT" : "AUTONOMOUS SYSTEM PIPELINE"}
              </div>
              <h3 className="text-xl sm:text-2xl font-bold font-serif text-white tracking-tight">
                {lang === "vi" ? "Luồng Xử Lý Tự Động: Cảm Biến → Quyết Định → Hành Động" : "Autonomous Behavioral Loop: Sensor → Decision → Action"}
              </h3>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-xs font-mono text-slate-400 bg-slate-900/90 px-3 py-1.5 rounded-lg border border-slate-800">
              <span className="text-emerald-400 font-semibold">CLOSED-LOOP</span>
              <span>CONTROL SYSTEM</span>
            </div>
          </div>

          {/* 3 Interactive Pipeline Stages */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
            {PIPELINE_STEPS.map((step, idx) => {
            const isLast = idx === PIPELINE_STEPS.length - 1;
            const StepIcon = step.icon;
            return <div key={step.id} data-index={idx} className="relative group">
                  <div className="h-full bg-slate-900/90 rounded-xl p-5 border border-slate-800/90 hover:border-sky-500/50 transition-all duration-300 flex flex-col justify-between relative z-10">
                    <div>
                      {/* Top Row: Phase Tag & Icon */}
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <span className="text-[11px] font-mono font-bold tracking-wider px-2.5 py-1 rounded bg-slate-800/90 text-sky-300 border border-slate-700">
                          {lang === "vi" ? step.phaseVi : step.phaseEn}
                        </span>
                        <div className="size-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-sky-400 group-hover:scale-105 group-hover:border-sky-400/60 transition-all">
                          <StepIcon className="size-5" />
                        </div>
                      </div>

                      {/* Title & Description */}
                      <h4 className="text-base sm:text-lg font-bold text-white mb-2 font-sans">
                        {lang === "vi" ? step.titleVi : step.titleEn}
                      </h4>
                      <p className="text-xs sm:text-sm text-slate-300 leading-relaxed mb-4">
                        {lang === "vi" ? step.descVi : step.descEn}
                      </p>
                    </div>

                    {/* Technical Module Specs */}
                    <div className="pt-3 border-t border-slate-800/80">
                      <span className="block text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">
                        {lang === "vi" ? "Mô-đun kỹ thuật:" : "Key Hardware/Code:"}
                      </span>
                      <p className="text-xs font-mono text-emerald-400/90 bg-emerald-950/40 p-2 rounded border border-emerald-900/50">
                        {lang === "vi" ? step.techVi : step.techEn}
                      </p>
                    </div>
                  </div>

                  {/* Desktop Connecting Arrow */}
                  {!isLast && <div className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 size-8 rounded-full bg-slate-800 border border-slate-700 items-center justify-center text-sky-400 shadow-md">
                      <ArrowRight className="size-4" />
                    </div>}

                  {/* Mobile Connecting Arrow */}
                  {!isLast && <div className="flex md:hidden justify-center my-3 relative z-20 text-sky-400">
                      <div className="size-8 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                        <ArrowDown className="size-4" />
                      </div>
                    </div>}
                </div>;
          })}
          </div>
        </motion.div>

        {/* 4-Column Grid for HP2 - HP5 */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-5">
          {COURSES_DATA.map((course, colIndex) => {
          const IconComponent = course.icon;
          return <motion.div key={course.code} data-index={colIndex} variants={cardVariants} className="h-full flex">
                <Card className={`relative flex flex-col w-full bg-card text-card-foreground rounded-xl border transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-1 ${course.accentColor}`}>
                  <CardHeader className="pb-4 pt-6 px-5 border-b border-border/60">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <Badge variant="outline" className={`font-semibold text-xs px-2.5 py-0.5 ${course.badgeStyle}`}>
                        {course.code}
                      </Badge>
                      <div className="size-9 rounded-lg bg-muted flex items-center justify-center text-foreground">
                        <IconComponent className="size-5" />
                      </div>
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground font-sans line-clamp-1">
                      {lang === "vi" ? course.titleVi : course.titleEn}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="flex-1 flex flex-col justify-between pt-6 pb-6 px-5">
                    {/* Vertical Concept Flow */}
                    <div className="space-y-2 relative">
                      {course.steps.map((step, stepIdx) => {
                    const isLast = stepIdx === course.steps.length - 1;
                    return <React.Fragment key={`${course.code}-step-${stepIdx}`}>
                            <div className="flex items-start gap-3 p-2.5 rounded-lg bg-muted/40 border border-border/40 hover:bg-muted/70 transition-colors">
                              <span className="flex items-center justify-center size-5 rounded-full bg-background border border-border text-[11px] font-semibold text-muted-foreground shrink-0 mt-0.5">
                                {stepIdx + 1}
                              </span>
                              <span className="text-xs sm:text-sm font-medium text-foreground leading-snug">
                                {lang === "vi" ? step.vi : step.en}
                              </span>
                            </div>

                            {!isLast && <div className="flex justify-center my-1">
                                <div className="p-1 rounded-full bg-muted text-muted-foreground/80">
                                  <ArrowDown className="size-3.5 animate-pulse" />
                                </div>
                              </div>}
                          </React.Fragment>;
                  })}
                    </div>

                    {/* Course Link CTA */}
                    <div className="mt-8 pt-4 border-t border-border/40">
                      <Button asChild variant="ghost" size="sm" className="w-full justify-between text-xs font-medium text-primary hover:text-primary hover:bg-primary/5 group">
                        <Link to={course.linkTo}>
                          <span>
                            {lang === "vi" ? `Xem khóa ${course.code}` : `View ${course.code} Course`}
                          </span>
                          <ChevronRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Pathway Banner */}
        <div className="mt-12 sm:mt-16 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-4 sm:px-6 rounded-2xl bg-card border border-border shadow-xs max-w-3xl mx-auto">
            <div className="size-10 rounded-full bg-secondary/20 text-secondary-foreground flex items-center justify-center shrink-0">
              <Sparkles className="size-5" />
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left flex-1">
              {lang === "vi" ? "Tìm hiểu lộ trình học Robotics toàn diện tại ROBOTO để chọn khóa học phù hợp nhất cho độ tuổi của con bạn." : "Explore our full Robotics Learning Pathway to find the perfect level for your child's age and experience."}
            </p>
            <Button asChild variant="default" size="sm" className="shrink-0 font-medium">
              <Link to="/RoboticsLearningPathwayPage">
                {lang === "vi" ? "Xem toàn bộ lộ trình" : "Full Learning Pathway"}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}